import React, { FC, useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  useQueryParams,
  JsonParam,
  NumberParam,
  withDefault,
} from 'use-query-params'
import { produce } from 'immer'
import clsx from 'clsx'
import { Icon } from '@iconify/react'
import {
  SortMenu,
  LiveProjectsFilter,
  useGlobalState,
  SwitchTabs,
  Button,
  PopOver,
  usePaginatedProjects,
} from '@pepper/ui'

import { useOrderItems } from 'utils/hooks/useOrderItems'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useDocItems } from 'utils/hooks/useDocItems'

import ViewToggleBtn from 'components/ViewToggleBtn/ViewToggleBtn'

import s from './_.module.css'

export type ViewType = 'project' | 'items' | 'feedback'

export type View = 'list' | 'grid'

type FilterOption = {
  title: string
  type: string
  props: {
    name: string
    list: any[]
  }
}

interface Props {
  tabName: ViewType
  filterOptions?: FilterOption[]
  showDataView: View
  setShowDataView?: React.Dispatch<React.SetStateAction<View>>
  searchValue: string | ''
  setSearchValue: (val: string) => void
  handleFilterReset?: () => void
  sortOptions: Record<string, any>
  children?: React.ReactNode
  projectType?: string
  handleStatusChange?: (value: string) => void
  isDisabled?: boolean
  onCreate: () => void
}

const LiveProjectsHeader: FC<Props> = ({
  tabName,
  filterOptions,
  showDataView,
  setShowDataView,
  handleFilterReset,
  sortOptions,
  children,
  isDisabled = false,
  onCreate,
}) => {
  const history = useHistory()
  const location = useLocation()
  const [queryParamFilters, setQueryParamFilters] = useQueryParams({
    filters: withDefault(JsonParam, {}),
    sort: withDefault(JsonParam, { sort_order: undefined, sort_by: undefined }),
    currentPage: withDefault(NumberParam, 0),
  })
  const [sortDropdownVisibility, setSortDropdownVisibility] = useState(false)
  const [showFilter, setshowFilter] = useState(false)
  const { permissions, viewport } = useGlobalState()
  const companyId = useSelectedCompanyId()
  const isMarketplaceEnabled = permissions?.marketplace

  const { total: totalOrders } = useOrderItems({
    companyId,
    pageSize: 1,
    queryParams: {
      stage: 'all',
    },
  })

  const { total: totalDocs } = useDocItems({
    companyId,
    pageSize: 1,
  })

  const { totalProjects } = usePaginatedProjects({
    company_id: companyId,
    page_size: 1,
  })

  const getSortText = (key: SortOptionType, selected: SortValueType) => {
    const option = Object.values(key).find(
      option =>
        option.order === selected?.sort_order &&
        option.sort_by === selected?.sort_by
    )
    return (
      Object.values(key).find(each => each.value === option?.value)?.label ??
      'Sort'
    )
  }

  const onSortChange = (sorts: SortValueType): void => {
    setSortDropdownVisibility(false)

    const nextFilters = produce(queryParamFilters, draftState => {
      draftState.sort = sorts
    })

    setQueryParamFilters({
      ...nextFilters,
      currentPage: 0,
    })
  }

  const onFilterChange = (
    filterName: string,
    filterValue: string[] | string
  ): void => {
    const updatedFilters = {
      ...queryParamFilters.filters,
      [filterName]: filterValue?.length ? filterValue : undefined,
    }
    const filtersWithValues = Object.values(updatedFilters).filter(
      v => v !== undefined
    )

    setQueryParamFilters({
      ...queryParamFilters,
      currentPage: 0,
      filters: filtersWithValues.length > 0 ? updatedFilters : undefined,
    })
  }

  const getFilterLength = () => {
    const result = []

    for (const key in queryParamFilters.filters) {
      if (queryParamFilters.filters[key].length === 0) continue
      result.push(key)
    }

    return result.length
  }

  //**to show filters when redirecting from home page as filter are applied */
  const filterLength = getFilterLength()

  const searchObj = new URLSearchParams(location.search)

  const handleTabChange = (tab: string) => {
    const itemType = `?item_types=${
      tab === 'docs' ? 'private_doc' : 'assignment'
    }&limit=${searchObj.get('limit')}`
    const search =
      tab === 'orders' || tab === 'docs'
        ? itemType
        : `?limit=${searchObj.get('limit')}`

    const pathname =
      tab === 'orders' || tab === 'docs' ? '/projects/items' : '/projects'

    history.push({ pathname, search })
  }

  const getTabList = () => {
    const tabList = [
      {
        title: `Projects (${totalProjects})`,
        value: 'project',
        data_id: 'projectView_projects',
      },
      {
        title: `Internal Docs (${totalDocs})`,
        value: 'docs',
        data_id: 'projectView_items',
      },
    ]
    if (!!isMarketplaceEnabled || !!totalOrders) {
      tabList.splice(1, 0, {
        title: `Orders (${totalOrders})`,
        value: 'orders',
        data_id: 'projectView_items',
      })
    }
    return tabList
  }

  useEffect(() => {
    if (filterLength === 1) {
      setshowFilter(true)
    }
  }, [filterLength])

  return (
    <div
      className={clsx({
        [s.inline]: tabName === 'items' && !permissions?.marketplace,
      })}
    >
      <div className={s.projectNumber}>
        <div className={s.switch}>
          <SwitchTabs
            activeTab={tabName}
            onTabChange={handleTabChange}
            tabList={getTabList()}
          />
        </div>
      </div>
      <div className={s.newProjectWrapper}>
        {viewport !== 'md' && (
          <ViewToggleBtn
            setShowDataView={setShowDataView}
            showDataView={showDataView}
            disabledView={tabName === 'items' ? 'grid' : undefined}
            isDisabled={isDisabled}
          />
        )}
        <div
          className={clsx(s.filters, {
            [s.noMargin]: showFilter,
          })}
        >
          {showFilter && filterOptions ? (
            <LiveProjectsFilter
              dropdownList={filterOptions}
              filters={queryParamFilters.filters}
              onFilterChange={onFilterChange}
              onResetFilter={() => {
                handleFilterReset?.()
                setshowFilter(false)
              }}
            />
          ) : (
            children
          )}
          <div className={s.icons}>
            {permissions?.marketplace && tabName === 'items' && (
              <Button
                type="default"
                className={`${s.filterTrigger} ${
                  showFilter ? s.showFilter : ''
                }`}
                onClick={() => setshowFilter(!showFilter)}
                icon={<Icon icon="lucide:sliders" />}
              >
                <span>Filter</span>
                {getFilterLength() > 0 && (
                  <span className={s.filtersCount}>{getFilterLength()}</span>
                )}
              </Button>
            )}
            <PopOver
              overlayClassName={s.popoverContainer}
              content={
                <SortMenu
                  defaultValue={queryParamFilters.sort}
                  options={sortOptions}
                  applyFilters={onSortChange}
                  onClose={(): void => setSortDropdownVisibility(false)}
                />
              }
              placement="bottomRight"
              trigger="click"
              visible={sortDropdownVisibility}
              onVisibleChange={(visible): void =>
                setSortDropdownVisibility(visible)
              }
            >
              <Button
                type="default"
                className={`${s.filterTrigger} ${
                  queryParamFilters.sort.sort_by ? s.sortSelected : ''
                }`}
                icon={<Icon icon="ant-design:sort-ascending-outlined" />}
                disabled={isDisabled}
              >
                <span>{getSortText(sortOptions, queryParamFilters.sort)}</span>
              </Button>
            </PopOver>

            {viewport !== 'md' && (
              <Button
                type="primary"
                icon={<Icon icon="lucide:plus" />}
                onClick={onCreate}
                data-id="new-project-button"
              >
                New Project
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LiveProjectsHeader
